<script setup lang="ts">
import type { IGenericSchoolSubject } from '@/interfaces';
import { getSubjectSoftColorPresentation, hexToRgba } from '@/utils/color.utils';
import { computed } from 'vue';

type TSchoolSubjectBadgeSize = 'chip' | 'sm' | 'md' | 'lg';
type TSchoolSubjectBadgeState = 'default' | 'selected' | 'muted';
type TSchoolSubjectBadgeAppearance = 'default' | 'soft';

defineSlots<{
  default?(): void;
}>();

const props = withDefaults(
  defineProps<{
    appearance?: TSchoolSubjectBadgeAppearance;
    schoolSubject: Pick<IGenericSchoolSubject, 'abbreviation' | 'color'>;
    size?: TSchoolSubjectBadgeSize;
    state?: TSchoolSubjectBadgeState;
    neutral?: boolean;
  }>(),
  {
    appearance: 'default',
    size: 'sm',
    state: 'default',
    neutral: false,
  },
);

const softColorPresentation = computed(() =>
  props.appearance === 'soft' && props.state === 'default' && !props.neutral
    ? getSubjectSoftColorPresentation(props.schoolSubject.color)
    : undefined,
);

const colorStyle = computed<Record<string, string> | undefined>(() => {
  if (props.state === 'selected') {
    return {
      backgroundColor: props.schoolSubject.color,
      color: 'white',
    };
  }

  if (props.neutral) return undefined;

  if (softColorPresentation.value) return undefined;

  return {
    backgroundColor: hexToRgba(props.schoolSubject.color, props.state === 'muted' ? 0.06 : 0.15),
    ...(props.state === 'muted' && { borderColor: props.schoolSubject.color }),
    color: props.schoolSubject.color,
  };
});
</script>

<template>
  <span
    class="flex shrink-0 items-center justify-center rounded-badge font-semibold transition duration-300"
    :class="[
      props.size === 'sm' && 'h-badge-sm min-w-badge-sm px-xs text-subject-badge uppercase',
      props.size === 'chip' && 'h-chip min-w-badge-sm px-smd text-footer-semibold',
      props.size === 'md' && 'h-badge-md min-w-badge-md px-smd text-body',
      props.size === 'lg' && 'h-subject-badge-lg min-w-subject-badge-lg px-smd text-body',
      props.state === 'muted' && 'border',
      props.neutral && props.state !== 'selected' && 'bg-surface-tertiary text-primary',
      softColorPresentation?.backgroundClass,
      softColorPresentation?.foregroundClass,
    ]"
    :style="colorStyle"
  >
    <slot>{{ props.schoolSubject.abbreviation }}</slot>
  </span>
</template>
